// Servizio per calcolo ricette offline
import ApiService from './api';

interface RicetteOfflineCache {
  gusti: any[];
  ricetteGusti: any[];
  ingredienti: any[];
  lastUpdate: number;
}

class RicetteOfflineService {
  private cache: RicetteOfflineCache | null = null;
  private readonly CACHE_KEY = 'gelato_ricette_offline';
  private readonly CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 ore

  // Verifica se i dati sono disponibili offline
  async isDataAvailable(): Promise<boolean> {
    await this.loadFromLocalStorage();
    return this.cache !== null && this.isDataFresh();
  }

  // Sincronizza i dati quando online
  async syncData(): Promise<boolean> {
    try {
      console.log('[RicetteOffline] Syncing data...');
      
      const [gusti, ricetteGusti, ingredienti] = await Promise.all([
        ApiService.getGusti(),
        this.fetchRicetteGusti(),
        ApiService.getIngredienti()
      ]);

      this.cache = {
        gusti,
        ricetteGusti,
        ingredienti,
        lastUpdate: Date.now()
      };

      this.saveToLocalStorage();
      console.log('[RicetteOffline] Data synced successfully');
      return true;
      
    } catch (error) {
      console.error('[RicetteOffline] Sync failed:', error);
      return false;
    }
  }

  // Fetch ricette gusti (non esiste nell'API principale, la creiamo)
  private async fetchRicetteGusti(): Promise<any[]> {
    try {
      // Se esiste un endpoint dedicato
      const response = await fetch('/api/ricette-gusti');
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.log('[RicetteOffline] No dedicated ricette-gusti endpoint, building from gusti');
    }

    // Altrimenti costruiamo dai gusti individuali
    try {
      const gusti = await ApiService.getGusti();
      const allRicette: any[] = [];

      for (const gusto of gusti) {
        try {
          const ricetta = await ApiService.getRicettaGusto(gusto.id);
          if (ricetta && ricetta.ingredienti && ricetta.ingredienti.length > 0) {
            allRicette.push(...ricetta.ingredienti.map((ing: any) => ({
              gusto_id: gusto.id,
              gusto_nome: gusto.nome,
              ingrediente_id: ing.ingrediente_id,
              quantita: ing.quantita,
              unita: ing.unita,
              quantita_per_kg: ing.quantita, // Assumiamo che sia già per kg
              is_variegatura: ing.is_variegatura || false,
              is_aggiunta: ing.is_aggiunta || false
            })));
          }
        } catch (error) {
          console.warn(`[RicetteOffline] Could not fetch ricetta for gusto ${gusto.id}`);
        }
      }

      return allRicette;
    } catch (error) {
      console.error('[RicetteOffline] Failed to build ricette from gusti:', error);
      return [];
    }
  }

  // Calcola ricette offline (versione semplificata senza costi)
  async calcolaRicetteOffline(gustiSelezionati: { gusto_id: number; quantita_kg: number }[]): Promise<any> {
    await this.loadFromLocalStorage();
    
    if (!this.cache) {
      throw new Error('Dati ricette non disponibili offline. Connettiti a internet per sincronizzare.');
    }

    const dettagliPerGusto: any[] = [];
    const ingredientiTotali = new Map();

    for (const gustoSel of gustiSelezionati) {
      const { gusto_id, quantita_kg } = gustoSel;
      
      if (!gusto_id || !quantita_kg || quantita_kg <= 0) {
        continue;
      }

      // Trova il gusto nella cache
      const gustoInfo = this.cache.gusti.find(g => g.id === gusto_id);
      if (!gustoInfo) {
        continue;
      }

      // Trova le ricette per questo gusto
      const ricetteGusto = this.cache.ricetteGusti.filter(r => r.gusto_id === gusto_id);

      if (ricetteGusto.length === 0) {
        // Gusto senza ricette
        dettagliPerGusto.push({
          gusto_id: gusto_id,
          gusto_nome: gustoInfo.nome,
          gusto_colore: gustoInfo.colore,
          quantita_kg: quantita_kg,
          ingredienti: [],
          ha_ricette: false
        });
        continue;
      }

      // Calcola ingredienti necessari (senza costi)
      const ingredientiGusto = ricetteGusto.map(ricetta => {
        const ingrediente = this.cache!.ingredienti.find(i => i.id === ricetta.ingrediente_id);
        const quantitaPerKg = parseFloat(ricetta.quantita || ricetta.quantita_per_kg || 0);
        const quantitaTotale = quantitaPerKg * quantita_kg;

        return {
          ingrediente_id: ricetta.ingrediente_id,
          ingrediente_nome: ingrediente?.nome || 'Ingrediente sconosciuto',
          ingrediente_unita: ingrediente?.unita_misura || ricetta.unita || 'g',
          quantita_per_kg: quantitaPerKg,
          unita_ricetta: ricetta.unita || 'g',
          quantita_necessaria: Math.round(quantitaTotale * 100) / 100,
          is_variegatura: ricetta.is_variegatura || false,
          is_aggiunta: ricetta.is_aggiunta || false
        };
      });

      dettagliPerGusto.push({
        gusto_id: gusto_id,
        gusto_nome: gustoInfo.nome,
        gusto_colore: gustoInfo.colore,
        quantita_kg: quantita_kg,
        ingredienti: ingredientiGusto,
        ha_ricette: true
      });

      // Accumula nelle quantità totali
      for (const ing of ingredientiGusto) {
        const key = ing.ingrediente_id;
        
        if (ingredientiTotali.has(key)) {
          const esistente = ingredientiTotali.get(key);
          esistente.quantita_totale += ing.quantita_necessaria;
        } else {
          ingredientiTotali.set(key, {
            ingrediente_id: ing.ingrediente_id,
            ingrediente_nome: ing.ingrediente_nome,
            ingrediente_unita: ing.ingrediente_unita,
            unita_ricetta: ing.unita_ricetta,
            quantita_totale: ing.quantita_necessaria
          });
        }
      }
    }

    // Converti la mappa dei totali in array
    const riepilogoIngredienti = Array.from(ingredientiTotali.values())
      .map(ing => ({
        ...ing,
        quantita_totale: Math.round(ing.quantita_totale * 100) / 100
      }))
      .sort((a, b) => b.quantita_totale - a.quantita_totale);

    const kgTotaliProduzione = dettagliPerGusto.reduce((sum, gusto) => sum + gusto.quantita_kg, 0);
    const gustiConRicette = dettagliPerGusto.filter(g => g.ha_ricette);
    const gustiSenzaRicette = dettagliPerGusto.filter(g => !g.ha_ricette);

    return {
      dettagli_per_gusto: dettagliPerGusto,
      riepilogo_ingredienti: riepilogoIngredienti,
      kg_totali: kgTotaliProduzione,
      num_gusti: dettagliPerGusto.length,
      num_gusti_con_ricette: gustiConRicette.length,
      num_gusti_senza_ricette: gustiSenzaRicette.length,
      num_ingredienti_diversi: riepilogoIngredienti.length,
      offline: true, // Flag per indicare calcolo offline
      message: 'Calcolo offline - costi non disponibili'
    };
  }

  // Carica dati dal localStorage
  private async loadFromLocalStorage(): Promise<void> {
    try {
      const stored = localStorage.getItem(this.CACHE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (this.isValidCache(data)) {
          this.cache = data;
        }
      }
    } catch (error) {
      console.error('[RicetteOffline] Error loading from localStorage:', error);
    }
  }

  // Salva dati nel localStorage
  private saveToLocalStorage(): void {
    try {
      if (this.cache) {
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(this.cache));
      }
    } catch (error) {
      console.error('[RicetteOffline] Error saving to localStorage:', error);
    }
  }

  // Verifica se i dati sono ancora freschi
  private isDataFresh(): boolean {
    if (!this.cache) return false;
    return (Date.now() - this.cache.lastUpdate) < this.CACHE_EXPIRY;
  }

  // Valida la struttura della cache
  private isValidCache(data: any): boolean {
    return data &&
           Array.isArray(data.gusti) &&
           Array.isArray(data.ricetteGusti) &&
           Array.isArray(data.ingredienti) &&
           typeof data.lastUpdate === 'number';
  }

  // Pulisci cache
  clearCache(): void {
    this.cache = null;
    localStorage.removeItem(this.CACHE_KEY);
  }

  // Ottieni info cache
  getCacheInfo(): { available: boolean; lastUpdate: Date | null; itemCount: number } {
    if (!this.cache) {
      return { available: false, lastUpdate: null, itemCount: 0 };
    }

    return {
      available: true,
      lastUpdate: new Date(this.cache.lastUpdate),
      itemCount: this.cache.gusti.length + this.cache.ricetteGusti.length + this.cache.ingredienti.length
    };
  }
}

export const ricetteOfflineService = new RicetteOfflineService();
